import { ReactNode, useState } from 'react'

import { zIndex } from '@avantstay/backoffice-core'

import {
  FloatingFocusManager,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'

import * as S from './DropdownButton.styles'

export interface DropdownButtonAction {
  label: ReactNode
  action: () => void
}

interface DropdownButtonProps {
  actions: [DropdownButtonAction, ...DropdownButtonAction[]]
  className?: string
  disabled?: boolean
  loading?: boolean
}

export const DropdownButton = ({ actions, className, disabled, loading }: DropdownButtonProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const [primaryAction, ...secondaryActions] = actions
  const hasMenu = secondaryActions.length > 0

  const { context, refs, floatingStyles } = useFloating<HTMLElement>({
    placement: 'top-end',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(8)],
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: !disabled }),
    useDismiss(context),
  ])

  const handleSecondaryAction = (action: () => void) => {
    setIsOpen(false)
    action()
  }

  return (
    <S.Root className={className}>
      <S.PrimaryButton
        type="button"
        $hasMenu={hasMenu}
        disabled={disabled}
        loading={loading}
        onClick={() => primaryAction.action()}
      >
        {primaryAction.label}
      </S.PrimaryButton>

      {hasMenu ? (
        <S.MenuToggle
          ref={refs.setReference}
          type="button"
          data-open={isOpen}
          data-testid="dropdown-button-toggle"
          disabled={disabled}
          {...getReferenceProps()}
        >
          <S.ToggleIcon $isOpen={isOpen} />
        </S.MenuToggle>
      ) : null}

      {isOpen ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <S.Menu
              ref={refs.setFloating}
              style={{ ...floatingStyles, zIndex: zIndex.GLOBAL_LOADING }}
              {...getFloatingProps()}
            >
              {secondaryActions.map((item, index) => (
                <S.MenuItem
                  key={index}
                  type="button"
                  onClick={() => handleSecondaryAction(item.action)}
                >
                  {item.label}
                </S.MenuItem>
              ))}
            </S.Menu>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </S.Root>
  )
}
